import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";
import { useIsClient } from "usehooks-ts";

import { ChatKind } from "@/api";
import { UnreadDot } from "@/components/doc/left-sidebar/UnreadDot";
import { Icon } from "@/components/icons/Icon";
import { Sidebar } from "@/components/sidebar";
import { useStore } from "@/store";
import { useChats } from "@/store/useChats";
import { formatCompactCount } from "@/utils/formatCompactCount";
import { trackEvent } from "@/utils/trackEvent";
import { twMerge } from "@/utils/twMerge";

import { ChatUnreadMessagesCount } from "./ChatUnreadMessagesCount";

export const ChatWithTeamButton = observer(function ChatWithTeamButton({
  className,
  disabled,
  isCollapsed,
}: {
  className?: string;
  disabled?: boolean;
  isCollapsed?: boolean;
}) {
  const { auth, dialog } = useStore();
  const chats = useChats();
  const isClient = useIsClient();
  const t = useTranslations();
  const isActive = chats.isOpenChat && chats.activeChatKind === ChatKind.collective;
  const totalCount = chats.totalMessagesCountForTeamChat;

  if (!isClient || !auth.isLoggedIn) {
    return null;
  }

  const handleClick = () => {
    if (!chats.canOpenTeamChat) {
      dialog.requestPermissionDialog.open();
      return;
    }
    if (isActive) {
      trackEvent("chat_with_team_closed", {
        source: "bottom_menu_button",
      });
      chats.closeChat();
    } else {
      trackEvent("chat_with_team_opened", {
        source: "bottom_menu_button",
      });
      chats.openChat(false);
    }
  };

  return (
    <Sidebar.Button
      disabled={disabled}
      className={twMerge("rounded-8 group w-fit", className)}
      wrapperClassName={twMerge(isCollapsed && "flex flex-col")}
      title={t("chat.openChatWithTeam")}
      hotkey="open-chat-with-team"
      icon={
        <Icon
          name="chat/Chat"
          width={24}
          height={24}
          className={twMerge(isActive && "[&>path]:stroke-icon-active")}
        />
      }
      onClick={handleClick}
      isActive={isActive}
      aria-pressed={isActive}
      // The button's name hides the message count's own label
      aria-description={totalCount > 0 ? t("chat.totalMessages", { count: totalCount }) : undefined}
      hintKey="teamChat"
    >
      {totalCount > 0 && (
        <>
          <div className="relative">
            <div
              aria-label={t("chat.totalMessages", { count: totalCount })}
              className={twMerge(
                "flex items-center justify-center rounded-[20px]",
                "px-[7px] py-[3px]",
                "text-base leading-none tracking-tight",
                "bg-gray-100 dark:bg-gray-900",
                "group-hover:bg-white dark:group-hover:bg-gray-800",
                isActive && "bg-white dark:bg-gray-800",
              )}
            >
              {formatCompactCount(totalCount)}
            </div>
            {!isCollapsed && (
              <ChatUnreadMessagesCount
                className="absolute z-10 -top-2 -right-3"
                count={chats.unreadMessagesCountForTeamChat}
              />
            )}
          </div>
          {isCollapsed && !!chats.unreadMessagesCountForTeamChat && (
            <UnreadDot className="absolute top-1 right-1" />
          )}
        </>
      )}
    </Sidebar.Button>
  );
});
